// Cómo se lee el enrolamiento del candado (paso 09, crédito): qué se le muestra al vendedor y
// cuándo se deja de preguntar.
import { ApiError } from '@/api/errors';
import { enrollmentFlow } from './enrollmentFlow';
import type { DeviceEnrollmentResult } from './types';

// La cadencia base vive con los flujos, junto al resto de lo que cambia por proveedor.
export { DEVICE_ENROLLMENT_POLL_MS } from './enrollmentFlow';

/** Cadencia del reintento del bono de crédito: mayor que su lease de 90 s, para que el siguiente tick lo retome. */
export const CREDIT_BONUS_RETRY_POLL_MS = 95_000;
/** Ventana del reintento desde que el bono quedó pendiente; después, el bono queda para conciliación. */
export const CREDIT_BONUS_RETRY_WINDOW_MS = 10 * 60_000;

/**
 * Los cinco desenlaces que el vendedor puede ver. Son CINCO y no cuatro porque el `ENROLLED` del
 * contrato se parte en dos: el proveedor registró el equipo, pero el candado todavía no reporta
 * señal de vida. Ese hueco dura minutos y es camino feliz — no se puede ver como falla ni como fin.
 */
export type EnrollmentView =
    | 'WAITING_INSTALL'
    | 'WAITING_LOCK_SIGNAL'
    | 'CONFIRMED'
    | 'PROVIDER_DOWN'
    | 'FAILED';

/** Qué mostrar, o `null` cuando todavía no hay expediente y el vendedor sigue capturando el IMEI. */
export function enrollmentView(result: DeviceEnrollmentResult | undefined): EnrollmentView | null {
    const enrollment = result?.enrollment;
    if (!enrollment) return null;
    switch (enrollment.status) {
        case 'FAILED':
            return 'FAILED';
        case 'PROVIDER_UNREACHABLE':
            return 'PROVIDER_DOWN';
        case 'ENROLLED':
            // Sin `creditStatus` el silo no corrió el activate-check: el candado no dio señal aún.
            return result.creditStatus == null ? 'WAITING_LOCK_SIGNAL' : 'CONFIRMED';
        default:
            return 'WAITING_INSTALL';
    }
}

/** Primer reintento tras una lectura fallida por algo pasajero; se duplica hasta el techo. */
export const ENROLLMENT_ERROR_BACKOFF_MS = 2000;
export const ENROLLMENT_ERROR_BACKOFF_MAX_MS = 15_000;

/** Mensajes con que `fetch` rechaza sin red en Chrome, Firefox y Safari. */
const NETWORK_FAILURE_MESSAGE = /failed to fetch|networkerror|load failed|network request failed/i;

/** Red caída, timeout o 5xx: vale volver a preguntar. Un 4xx o un bug del front no cambian por insistir. */
export function isTransientRequestError(error: unknown): boolean {
    if (error instanceof ApiError) return error.httpStatus >= 500;
    if (error instanceof TypeError) return NETWORK_FAILURE_MESSAGE.test(error.message);
    return error instanceof Error && error.name === 'RequestTimeoutError';
}

/** Espera tras `failures` lecturas fallidas seguidas: 2 s, 4 s, 8 s y luego 15 s. */
export function enrollmentErrorBackoffMs(failures: number): number {
    const exponent = Math.max(0, failures - 1);
    return Math.min(ENROLLMENT_ERROR_BACKOFF_MS * 2 ** exponent, ENROLLMENT_ERROR_BACKOFF_MAX_MS);
}

/** Cada cuánto repreguntar o `false`: corta en FAILED y en 4xx, backoff ante fallos pasajeros. En
 * CONFIRMED sigue lento solo con el bono de crédito RESERVED y en su ventana. */
export function deviceEnrollmentPollInterval(
    result: DeviceEnrollmentResult | undefined,
    queryError: unknown,
    consecutiveFailures = 0,
    now: number = Date.now(),
): number | false {
    if (queryError != null) {
        return isTransientRequestError(queryError)
            ? enrollmentErrorBackoffMs(consecutiveFailures)
            : false;
    }
    const view = enrollmentView(result);
    if (view === null || view === 'FAILED') return false;
    if (view === 'CONFIRMED') return creditBonusRetryInterval(result, now);
    return enrollmentFlow(result?.mdmProvider).pollMs;
}

/** Tras confirmar: la ventana se mide desde el `pendingSince` del servidor, no desde el montaje. */
function creditBonusRetryInterval(
    result: DeviceEnrollmentResult | undefined,
    now: number,
): number | false {
    const bonus = result?.creditPromotionBonus;
    if (result?.creditStatus !== 'ACTIVE' || bonus?.status !== 'RESERVED' || !bonus.pendingSince)
        return false;
    const pendingAt = Date.parse(bonus.pendingSince);
    return now - pendingAt < CREDIT_BONUS_RETRY_WINDOW_MS ? CREDIT_BONUS_RETRY_POLL_MS : false;
}
